import { useEffect } from "react";
import { isConflict } from "../../api/errors";
import { useDeployPreview, useRollbackDeployment } from "../../api/queries";
import type { Deployment } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { Spinner } from "../../components/AsyncStates";
import { useToast } from "../../toast/ToastContext";
import { formatRelativeTime, shortDigest } from "./deployFormat";
import "./services.css";

interface RollbackDialogProps {
  projectId: string;
  serviceId: string;
  serviceName: string;
  /** The deployment being rolled back TO — a past attempt that reached `"running"` (the
   * server refuses any other target, ADR-0031 §2b). `null` means the dialog is closed —
   * same "driven by the target" convention as `DeploymentDetailModal`/`DeletePlacementDialog`. */
  target: Deployment | null;
  /** The service's currently active/most-recent deployment, when known — shown as the
   * "from" side of the revision line for context (e.g. "the failing deploy"). Omitted when
   * the caller has no better context than the target itself (still a complete, honest
   * dialog either way — A-10: never fabricate a "from" state that wasn't actually passed in). */
  fromDeployment?: Deployment;
  onClose: () => void;
}

/**
 * The ONE rollback surface (IA slice 4, RollbackRisk board) — reused by the service header's
 * "Rollback…" action and the Deployments history list's per-row "Roll back to this" (see
 * `ServiceDetailPage`/`DeploymentTimeline`). Evolves what used to be `DeploymentDetailModal`'s
 * inline rollback confirm step into a purpose-built dialog carrying the real volume-risk
 * preview (`useDeployPreview`, ADR-0031 §2d) — `DeploymentDetailModal` now handles redeploy
 * only, so there is exactly one rollback entry point in the UI, not two.
 *
 * "signed-by-ci" is deliberately NOT rendered here (unlike the RollbackRisk mock's ✓ badge):
 * `Deployment` carries no provenance/signature field today (A-10 — no field, no badge; see
 * this slice's report for the full grounding note).
 */
export function RollbackDialog({ projectId, serviceId, serviceName, target, fromDeployment, onClose }: RollbackDialogProps) {
  const { pushToast } = useToast();
  const rollbackMutation = useRollbackDeployment(projectId, serviceId);
  const preview = useDeployPreview(projectId, serviceId, target !== null);

  // A fresh target (a different deployment, or the dialog re-opening) starts with no stale
  // mutation error from a previous rollback attempt.
  useEffect(() => {
    rollbackMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  function handleClose() {
    rollbackMutation.reset();
    onClose();
  }

  function handleConfirm() {
    if (!target) return;
    rollbackMutation.mutate(
      { deploymentId: target.id },
      {
        onSuccess: () => {
          pushToast({ title: "Rollback started.", tone: "success" });
          handleClose();
        },
        // Inline banner below carries the server's own refusal — leave the dialog open.
      },
    );
  }

  // Real read-write named volumes only (never fabricated) — a placement whose plan has none
  // reports neither `volumeRisk` nor `volumeWarning` (see `PlacementPreview`'s doc comment).
  const atRiskPlacements = (preview.data?.placements ?? []).filter((p) => (p.volumeRisk?.length ?? 0) > 0);
  const riskyVolumeNames = Array.from(new Set(atRiskPlacements.flatMap((p) => p.volumeRisk ?? [])));

  return (
    <Modal open={target !== null} title={`Roll back ${serviceName}`} onClose={handleClose}>
      {target && (
        <div className="rollback-dialog">
          <p className="rollback-dialog__lede">
            Redeploys this service to a prior running revision through the normal rolling
            machinery. Every step is audited.
          </p>

          {rollbackMutation.isError && rollbackMutation.error && (
            <Banner tone={isConflict(rollbackMutation.error) ? "warning" : "danger"} title="Rollback not available">
              {rollbackMutation.error.message || "Try again in a moment."}
            </Banner>
          )}

          <div className="rollback-dialog__revision">
            <div className="rollback-dialog__revision-line data-table__mono">
              {fromDeployment && (
                <>
                  <span className="rollback-dialog__revision-from">
                    {fromDeployment.digest ? shortDigest(fromDeployment.digest) : fromDeployment.image || "current"}
                  </span>
                  <span aria-hidden="true"> → </span>
                </>
              )}
              <span className="rollback-dialog__revision-to">
                {target.digest ? shortDigest(target.digest) : target.image || `deployment ${target.id}`}
              </span>
            </div>
            <p className="rollback-dialog__revision-detail">
              Target: deployed{" "}
              <time dateTime={target.createdAt} title={new Date(target.createdAt).toLocaleString()}>
                {formatRelativeTime(target.createdAt)}
              </time>{" "}
              by {target.createdBy || "—"}
            </p>
          </div>

          {preview.isPending && target && (
            <p className="rollback-dialog__preview-loading">
              <Spinner label="Checking for data-safety risk…" />
            </p>
          )}

          {riskyVolumeNames.length > 0 && (
            <div className="rollback-dialog__volume-risk" role="note">
              <p className="rollback-dialog__volume-risk-title">Data written since the target revision</p>
              <p>
                <span className="data-table__mono">{riskyVolumeNames.join(", ")}</span> may have received writes
                since. Rolling back the code keeps the volume — data is retained by default; nothing is deleted.
              </p>
              <p className="rollback-dialog__volume-risk-caution">
                If the new revision migrated the schema, the old code may not read it — check before confirming.
              </p>
            </div>
          )}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={rollbackMutation.isPending}>
              Cancel
            </Button>
            <Button type="button" variant="danger" onClick={handleConfirm} disabled={rollbackMutation.isPending}>
              {rollbackMutation.isPending ? "Rolling back…" : "Roll back now"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
